import React from 'react';
import Markdown from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { QBox } from '@captivateiq/quotient';
import type { Message } from '@captivateiq/assistant/types';

type AgentMessageProps = {
  message: Message;
};

const sanitizationSchema = {
  ...defaultSchema,
  tagNames: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'del',
    's',
    'mark',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'blockquote',
    'hr',
    'code',
    'pre',
  ],
};

const defaultMarkdownComponents = {
  table: ({ children }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <QBox
      sx={{
        width: '100%',
        overflowX: 'auto',
        marginBottom: '1rem',
        scrollbarWidth: 'thin',
        // Horizontal scroll shadows with theme color
        backgroundImage: `
          linear-gradient(90deg, rgba(251,251,251,1) 30%, rgba(251,251,251,0)),
          linear-gradient(90deg, rgba(251,251,251,0), rgba(251,251,251,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '30px 100%, 30px 100%, 10px 100%, 10px 100%',
        backgroundPosition: '0 0, 100% 0, 0 0, 100% 0',
        backgroundAttachment: 'local, local, scroll, scroll',
        '.statement-elevated-section &': {
          // on white background
          backgroundImage: `
          linear-gradient(90deg, rgba(255,255,255,1) 30%, rgba(255,255,255,0)),
          linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1) 70%),
          radial-gradient(farthest-side at 0% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0))
        `,
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
        },
      }}
    >
      <table>{children}</table>
    </QBox>
  ),
};

export const AgentMessage = ({ message }: AgentMessageProps) => {
  return (
    <QBox
      sx={{
        textAlign: 'left',
        pb: 4,
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          '&:hover': { color: 'primary.dark' },
        },
        '& p': {
          margin: '0 0 1rem 0',
          '&:last-child': {
            marginBottom: 0,
          },
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '1.5rem 0 1rem 0',
          '&:first-of-type': {
            marginTop: 0,
          },
        },
        '& h1': { fontSize: '1.5rem', fontWeight: 600 },
        '& h2': { fontSize: '1.25rem', fontWeight: 600 },
        '& h3': { fontSize: '1.125rem', fontWeight: 600 },
        '& h4': { fontSize: '1rem', fontWeight: 600 },
        '& h5': { fontSize: '0.875rem', fontWeight: 600 },
        '& h6': { fontSize: '0.75rem', fontWeight: 600 },
        '& ul, & ol': {
          margin: '0 0 1rem 0',
          paddingLeft: '2rem',
        },
        '& li': {
          marginBottom: '0.25rem',
        },
        '& hr': {
          border: 'none',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          margin: '1rem 0',
        },
        '& table': {
          borderCollapse: 'collapse',
          marginBottom: '1rem',
          width: '100%',
        },
        '& td, & th': {
          padding: '0.5rem 1rem',
        },
        '& th': {
          fontWeight: 'normal',
        },
        '& thead': {
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        },
        '& blockquote': {
          borderLeft: '4px solid',
          borderLeftColor: 'grey.300',
          margin: '1rem 0',
          paddingLeft: '1rem',
          paddingTop: '0.25rem',
          paddingBottom: '0.25rem',
          color: 'grey.600',
          fontStyle: 'italic',
          backgroundColor: 'grey.50',
        },
      }}
    >
      <Markdown
        components={defaultMarkdownComponents}
        rehypePlugins={[[rehypeSanitize, sanitizationSchema]]}
        remarkPlugins={[remarkGfm]}
      >
        {message.contents}
      </Markdown>
    </QBox>
  );
};
